import { useRequest } from '@byted/hooks';
import { Toast, Modal, Select, Input, Form } from '@douyinfe/semi-ui';
import { useRef, useState } from 'react';
import { FormApi } from '@douyinfe/semi-ui/lib/es/form';
import { toastError } from '@/utils/toastError';
import { forumPostService } from '@/api/forumPost';
import { Biz } from '@/typings/open.platform.forum_bff/namespaces/configure';
import { GroupType } from '@/typings/open.platform.forum_bff/namespaces/forumpost';
import { Scope } from '@/typings/fe';
import useBiz from '@/context/useBiz';
import { useScope } from '@/hooks/useScope';

export function useDeleteGroup() {
  const { loading: deletingGroup, run: deleteGroup } = useRequest(
    async (groupId: string, biz: Biz) => {
      try {
        const res = await forumPostService.deleteGroup({
          Biz: biz,
          Uid: '',
          GroupId: groupId,
        });
        if (res.error) {
          throw new Error(res.message);
        }
      } catch (e) {
        toastError(e);
      }
    },
    {
      auto: false,
    },
  );
  return { deleteGroup, deletingGroup };
}

export function useInsertPostGroup() {
  const biz = useBiz(s => s.biz);
  const { run: insertPostGroup, loading: addingPost } = useRequest(
    async (groupId: string, postId: string, order?: number) => {
      try {
        const res = await forumPostService.insertPostGroup({
          GroupId: groupId,
          PostId: postId,
          Uid: '',
          Biz: biz,
          Index: order,
        });
        if (res.error) {
          throw new Error(res.message);
        }
      } catch (e) {
        toastError(e);
      }
    },
    {
      auto: false,
    },
  );
  return { insertPostGroup, addingPost };
}

export function useInsertGroup() {
  const { loading: addingGroup, run: addGroup } = useRequest(
    async (name: string, type: GroupType, biz: Biz, scope: Scope) => {
      try {
        const res = await forumPostService.insertGroup({
          Uid: '',
          Biz: biz,
          Scope: scope,
          Name: name,
          GroupType: type,
        });
        if (res.error) {
          throw new Error(res.message);
        }
      } catch (e) {
        toastError(e);
      }
    },
    {
      auto: false,
    },
  );
  return { addingGroup, insertGroup: addGroup };
}

export function useDeletePostGroup() {
  const biz = useBiz(s => s.biz);
  const { run: deletePostGroup, loading: removingPost } = useRequest(
    async (postId: string, groupId: string) => {
      try {
        const res = await forumPostService.deletePostGroup({
          Uid: '',
          GroupId: groupId,
          PostId: postId,
          Biz: biz,
        });
        if (res.error) {
          throw new Error(res.message);
        }
      } catch (e) {
        toastError(e);
      }
    },
    {
      auto: false,
    },
  );
  return { deletePostGroup, removingPost };
}

export function useGetPostsByGroup() {
  const scope = useScope();
  const {
    data: posts,
    run: getPostsByGroup,
    loading,
  } = useRequest(
    async () => {
      try {
        const res = await forumPostService.getPostsByGroup({
          Scope: scope,
        });
        if (res.error) {
          throw new Error(res.message);
        }
        return res.data;
      } catch (e) {
        toastError(e);
      }
      return undefined;
    },
    {
      auto: true,
      refreshDeps: [scope],
      // Trick 销毁社区子应用后不发起请求
      ready: window.location.pathname.includes('forum'),
    },
  );

  return { getPostsByGroup, loading, posts };
}

export const useInsertPostGroupModal = () => {
  const { insertPostGroup } = useInsertPostGroup();

  return {
    renderAddPostModal(
      id: string,
      refetch: () => Promise<void>,
      orderCount = 4,
    ) {
      let order: number | undefined;
      let postId: string | undefined;
      Modal.info({
        title: '添加帖子',
        icon: null,
        content: (
          <div className="">
            <Input
              className="w-full mb-2"
              placeholder={'请输入帖子 ID'}
              onChange={val => {
                postId = val;
              }}
            />
            <Select
              className="w-full mb-2"
              placeholder={'请选择帖子排序'}
              onChange={val => {
                order = Number(val);
              }}
              optionList={new Array(orderCount).fill(0).map((_, i) => ({
                label: i + 1,
                value: i + 1,
              }))}
            />
            <div className="text-semi-color-text-2">
              未选择则在看板按时间顺序排序
            </div>
          </div>
        ),
        async onOk() {
          await insertPostGroup(id, postId ?? '', order);
          await refetch();
          Toast.success('添加成功');
        },
      });
    },
  };
};

export const useInsertGroupModal = () => {
  const { insertGroup } = useInsertGroup();
  const [visible, setVisible] = useState(false);
  const formRef = useRef<FormApi>();
  const biz = useBiz(s => s.biz);
  const scope = useScope();
  const [groupType, setGroupType] = useState<number>(0);
  const title = groupType === GroupType.Special ? '添加专题' : '添加面板';
  const placeHolder =
    groupType === GroupType.Special ? '请输入专题名称' : '请输入面板名称';
  const ok = async () => {
    await formRef.current?.validate();
    const name = formRef.current?.getValue('name');
    await insertGroup(name, groupType, biz, scope);
    await refetchRef.current?.();
    Toast.success('添加成功');
  };
  const refetchRef = useRef<() => Promise<void>>();
  return {
    triggerAddModal(type: GroupType, refetch: () => Promise<void>) {
      setGroupType(type);
      refetchRef.current = refetch;
      setVisible(true);
    },
    renderInsertGroupModal() {
      return (
        <Modal
          title={title}
          closeOnEsc={true}
          visible={visible}
          onOk={async () => {
            await ok();
            setVisible(false);
          }}
          onCancel={() => {
            setVisible(false);
          }}>
          <Form
            getFormApi={api => {
              formRef.current = api;
            }}>
            <Form.Input
              field="name"
              noLabel={true}
              autofocus={true}
              className="w-full mb-2"
              validate={value => {
                if (typeof value !== 'string' || !value?.length) {
                  return '名称不能为空';
                }
                if (value.length > 10) {
                  return '名称长度不能超过 10';
                }
                return '';
              }}
              onEnterPress={async () => {
                await ok();
                setVisible(false);
              }}
              initValue={''}
              placeholder={placeHolder}
            />
          </Form>
        </Modal>
      );
    },
  };
};
